/**
 * Create and edit a task.
 *
 * The project is fixed once the task exists — the API refuses to move a task
 * between projects, because its booked time would follow it onto another
 * client's bill. So the picker appears on create and a statement of fact
 * appears on edit.
 *
 * The milestone list depends on the chosen project and is refetched when it
 * changes. A milestone from another project is refused server-side; narrowing
 * the list here just stops somebody being offered the refusal.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  RecordSelect,
  Select,
  Switch,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

const schema = z.object({
  title: z.string().trim().min(1, 'A task needs a title.').max(160),
  status: z.enum(['todo', 'in_progress', 'blocked', 'review', 'done', 'cancelled']),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  due_on: z.string().optional(),
  // Hours in, minutes out. Stored as minutes because that is the unit time is
  // booked in, and a fractional hour stored as a float is a rounding argument.
  estimate_hours: z.string().trim().regex(/^\d*\.?\d?$/, 'Hours, to one decimal.').optional(),
  description: z.string().trim().optional(),
  billable: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface TaskRecord {
  public_id: string;
  code: string;
  title: string;
  status: string;
  priority: string;
  project_public_id: string;
  project_name: string;
  assignee_name: string | null;
  assignee_id: string | null;
  milestone_name: string | null;
  milestone_id: string | null;
  due_on: string | null;
  estimate_minutes: number | null;
  description: string | null;
  billable: boolean;
}

interface Milestone {
  public_id: string;
  name: string;
  status: string;
}

const DEFAULTS: FormValues = {
  title: '',
  status: 'todo',
  priority: 'normal',
  due_on: '',
  estimate_hours: '',
  description: '',
  billable: true,
};

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'review', label: 'In review' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function TaskFormDialog({
  open,
  onClose,
  onSaved,
  publicId,
  projectPublicId,
  projectName,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: (publicId: string) => void;
  publicId?: string;
  projectPublicId?: string;
  projectName?: string;
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(publicId);

  const [project, setProject] = useState<string | null>(projectPublicId ?? null);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [milestone, setMilestone] = useState<string | null>(null);

  const existing = useQuery({
    queryKey: ['tasks', publicId],
    queryFn: () => api.get<TaskRecord>(`/tasks/${publicId}`),
    enabled: open && editing,
  });

  const milestones = useQuery({
    queryKey: ['projects', project, 'milestones'],
    queryFn: () => api.get<Milestone[]>(`/projects/${project}/milestones`),
    enabled: open && project !== null,
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    if (editing && existing.data) {
      const row = existing.data;
      form.reset({
        title: row.title,
        status: row.status as FormValues['status'],
        priority: row.priority as FormValues['priority'],
        due_on: row.due_on ?? '',
        estimate_hours: row.estimate_minutes ? String(row.estimate_minutes / 60) : '',
        description: row.description ?? '',
        billable: row.billable,
      });
      setProject(row.project_public_id);
      setAssignee(row.assignee_id);
      setMilestone(row.milestone_id);
    } else if (!editing) {
      form.reset(DEFAULTS);
      setProject(projectPublicId ?? null);
      setAssignee(null);
      setMilestone(null);
    }
  }, [open, editing, existing.data, form, projectPublicId]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const { estimate_hours, ...rest } = values;
      const payload: Record<string, unknown> = Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [
          key,
          typeof value === 'string' && value.trim() === '' ? null : value,
        ]),
      );
      payload.estimate_minutes = estimate_hours ? Math.round(Number(estimate_hours) * 60) : null;
      payload.assignee_id = assignee;
      payload.milestone_id = milestone;

      if (editing) return api.patch<TaskRecord>(`/tasks/${publicId}`, payload);
      return api.post<TaskRecord>(`/tasks/for-project/${project}`, payload);
    },
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success(editing ? 'Task updated' : 'Task created', record.code);
      onSaved?.(record.public_id);
      if (editing) onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (!editing && project === null) {
      form.setError('root', { message: 'Choose the project this task belongs to.' });
      return;
    }
    void form.handleSubmit((values) => save.mutateAsync(values))();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit task' : 'New task'}
      size="lg"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} onClick={submit}>
            {editing ? 'Save changes' : 'Create task'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        {editing ? (
          <Alert tone="info">
            On {existing.data?.project_name ?? 'its project'}. A task cannot be moved between
            projects — its booked time would move with it.
          </Alert>
        ) : (
          <RecordSelect
            label="Project"
            required
            endpoint="/projects"
            value={project}
            valueLabel={projectName}
            onChange={(next) => {
              setProject(next);
              // The milestone belonged to the old project, so it cannot survive
              // the change. Clearing it here saves a refusal from the server.
              setMilestone(null);
            }}
            toOption={(row) => ({
              value: String(row.public_id),
              label: String(row.name),
              detail: String(row.client_name ?? ''),
            })}
            placeholder="Search projects…"
          />
        )}

        <Input
          label="Title"
          required
          error={form.formState.errors.title?.message}
          {...form.register('title')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            error={form.formState.errors.status?.message}
            {...form.register('status')}
          />
          <Select
            label="Priority"
            options={PRIORITY_OPTIONS}
            error={form.formState.errors.priority?.message}
            {...form.register('priority')}
          />
          <RecordSelect
            label="Assignee"
            endpoint="/hr/employees"
            value={assignee}
            valueLabel={editing ? existing.data?.assignee_name : null}
            onChange={(next) => setAssignee(next)}
            toOption={(row) => ({
              value: String(row.public_id),
              label: String(row.full_name),
              detail: String(row.job_title ?? ''),
            })}
            placeholder="Nobody yet"
            hint="Needs the People service. Leave empty to assign later."
          />
          <Select
            label="Milestone"
            options={[
              { value: '', label: 'None' },
              ...(milestones.data ?? []).map((m) => ({ value: m.public_id, label: m.name })),
            ]}
            value={milestone ?? ''}
            onChange={(event) => setMilestone(event.target.value || null)}
            hint={project === null ? 'Choose a project first.' : undefined}
            disabled={project === null}
          />
          <Input
            label="Due"
            type="date"
            error={form.formState.errors.due_on?.message}
            {...form.register('due_on')}
          />
          <Input
            label="Estimate (hours)"
            inputMode="decimal"
            placeholder="0"
            error={form.formState.errors.estimate_hours?.message}
            {...form.register('estimate_hours')}
          />
        </div>

        <Textarea
          label="Description"
          rows={3}
          error={form.formState.errors.description?.message}
          {...form.register('description')}
        />

        <Switch
          label="Billable"
          hint="Time booked to a non-billable task never reaches an invoice, whatever the project says."
          {...form.register('billable')}
        />
      </form>
    </Modal>
  );
}
